import { z } from "zod";
import { easingSchema } from "./primitives.js";

export const transitionTypes = [
  "none",
  "fade",
  "crossfade",
  "slide-left",
  "slide-right",
  "slide-up",
  "slide-down",
  "zoom",
  "blur",
  "wipe",
  "circle-reveal",
] as const;

export const transitionSchema = z.object({
  type: z.enum(transitionTypes).default("fade"),
  /** Seconds of overlap between the outgoing and incoming section. */
  duration: z.number().min(0).max(5).default(0.6),
  easing: easingSchema.default("ease-in-out"),
});

/**
 * How long a transition runs.
 *
 * Three named paces rather than a slider. The slider offered fifty values
 * between nought and two and a half seconds to answer a question that only ever
 * has three useful answers, and because transitions overlap their neighbours,
 * every one of those fifty values was also a different runtime for the finished
 * video.
 */
export const transitionPaces = [
  { name: "Quick", seconds: 0.35 },
  { name: "Normal", seconds: 0.6 },
  { name: "Slow", seconds: 1.2 },
] as const;

/**
 * The easing every transition uses.
 *
 * `easing` stays on the schema because the renderer reads it and because
 * timelines already carry a value, but it is no longer something anyone is
 * asked about: it is an expert control whose effect is close to invisible on
 * the transitions people actually pick, and slow at both ends is right for all
 * of them. A transition edited from the editor is written with this value, so a
 * project converges on it as it is worked on rather than all at once.
 */
export const standardEasing = "ease-in-out" as const;

export type Transition = z.infer<typeof transitionSchema>;
export type TransitionType = (typeof transitionTypes)[number];
